import { useState } from 'react'
import { ChartFrame, ChartTooltip, type TooltipState, ValueAxis } from './Chart'
import {
  PLOT,
  type Series,
  axisNumber,
  bandCenter,
  ceiling,
  inner,
  labelStride,
  niceTicks,
  seriesColor
} from './scale'

/**
 * Change over time, as lines or as a filled area.
 *
 * A crosshair rather than per-point hit targets: on a fortnight of daily values the points are eight
 * pixels apart, and asking somebody to land on one is asking them to fail. The crosshair reads the
 * nearest column and shows every series at once, which is also the comparison the chart exists to
 * support — "did detections rise on the day events did".
 */
export function LineChart({
  title,
  description,
  categories,
  labels,
  series,
  filled = false
}: {
  title: string
  description: string
  /** The x values, one per point, used as table row headings. */
  categories: string[]
  /** What the axis shows for each category — usually a shortened date. */
  labels: string[]
  series: Series[]
  filled?: boolean
}) {
  const [hover, setHover] = useState<TooltipState | null>(null)
  const box = inner(PLOT)

  const highest = Math.max(1, ...series.flatMap((entry) => entry.values))
  const top = ceiling(highest)
  const ticks = niceTicks(highest)
  const y = (value: number) => box.bottom - (value / top) * box.height
  const x = (index: number) => bandCenter(index, categories.length, box.left, box.width)
  const stride = labelStride(categories.length, box.width)

  function onMove(event: React.PointerEvent<SVGSVGElement>) {
    const bounds = event.currentTarget.getBoundingClientRect()
    if (bounds.width === 0 || categories.length === 0) return

    // Back out of the browser's scaling into plot coordinates, then to the nearest column.
    const withinPlot = ((event.clientX - bounds.left) / bounds.width) * PLOT.width
    const fraction = (withinPlot - box.left) / box.width
    const index = Math.min(categories.length - 1, Math.max(0, Math.round(fraction * categories.length - 0.5)))

    setHover({
      x: x(index) / PLOT.width,
      // Centerd on the plot rather than pinned to its top edge: the tooltip is translated by -50%
      // vertically, so anchoring it at the top would leave half of it above the chart and over the
      // legend.
      y: 0.5,
      heading: categories[index] ?? '',
      rows: series.map((entry, slot) => ({
        label: entry.label,
        value: entry.values[index] ?? 0,
        color: seriesColor(slot)
      }))
    })
  }

  return (
    <ChartFrame
      title={title}
      description={description}
      categories={categories}
      series={series}
      hideLegend={series.length < 2}
    >
      <div className="chart-surface">
        <svg
          viewBox={`0 0 ${PLOT.width} ${PLOT.height}`}
          preserveAspectRatio="xMidYMid meet"
          className="chart-svg"
          onPointerMove={onMove}
          onPointerLeave={() => setHover(null)}
        >
          <ValueAxis ticks={ticks} scale={y} left={box.left} right={box.right} format={axisNumber} />

          {hover && (
            <line
              className="chart-crosshair"
              x1={hover.x * PLOT.width}
              x2={hover.x * PLOT.width}
              y1={box.top}
              y2={box.bottom}
            />
          )}

          {series.map((entry, slot) => {
            const points = entry.values.map((value, index) => `${x(index)},${y(value)}`)
            const color = seriesColor(slot)

            return (
              <g key={entry.key}>
                {filled && points.length > 1 && (
                  <polygon
                    // A wash rather than a block: an area at full strength hides whatever is behind it
                    // and makes two overlapping series unreadable.
                    fill={color}
                    fillOpacity={0.1}
                    stroke="none"
                    points={`${x(0)},${box.bottom} ${points.join(' ')} ${x(entry.values.length - 1)},${box.bottom}`}
                  />
                )}
                <polyline
                  fill="none"
                  stroke={color}
                  strokeWidth={2}
                  strokeLinejoin="round"
                  strokeLinecap="round"
                  points={points.join(' ')}
                />
                {/* The end dot marks where the series finishes, and its surface ring keeps it legible
                    where two series cross. Only the last point is marked: a dot on every value is the
                    clutter that stops anybody reading the line. */}
                {entry.values.length > 0 && (
                  <circle
                    cx={x(entry.values.length - 1)}
                    cy={y(entry.values[entry.values.length - 1] ?? 0)}
                    r={4}
                    fill={color}
                    className="chart-marker"
                  />
                )}
              </g>
            )
          })}

          <g className="chart-axis" aria-hidden="true">
            {labels.map((label, index) =>
              index % stride === 0 ? (
                <text key={`${label}-${index}`} x={x(index)} y={box.bottom + 18} textAnchor="middle" className="chart-tick">
                  {label}
                </text>
              ) : null
            )}
          </g>
        </svg>
        <ChartTooltip state={hover} />
      </div>
    </ChartFrame>
  )
}
